// src/utils/dice.js
//
// Single source of truth for the Deathwatch d100 test engine: the percentile
// roll, Degrees of Success/Failure, the six-tier difficulty ladder, target
// clamping, fate-point application, and the combat math (dice expression
// parsing, hit counts from Degrees, hit-location table, damage mitigation).
//
// It is CommonJS on purpose: the Express backend (Node, CJS) `require()`s it,
// and the CRA client (webpack) interops the named exports so ESM components can
// `import { d100, degrees, ... } from '../utils/dice'`. The two pre-existing
// ESM modules (src/utils/diceRoller.js and src/components/rollHelpers.js) are
// thin re-export shims over THIS file, so the ~3300-line components keep their
// current import paths with no signature change, and there is exactly ONE
// implementation of the dice math in the repo.


// ── Percentile roll ──────────────────────────────────────────────────────────
// 1..100 inclusive (percentile).
function d100() {
  return Math.floor(Math.random() * 100) + 1;
}

// ── Degrees of Success / Failure, with criticals ─────────────────────────────
// A test is scored "roll at or under target". The distance from the target is
// measured in Degrees: a success at target is 1 DoS and each further full 10
// below the target adds one more; failures score symmetric Degrees of Failure.
// A NATURAL 1 is a critical failure and a NATURAL 100 a critical success,
// independent of the target number (Deathwatch rules; the previous engine
// treated them as ordinary rolls).
function degrees(target, roll) {
  if (roll === 1) return { success: false, dos: 0, dof: 1, critical: 'fumble' };
  if (roll === 100) return { success: true, dos: 1, dof: 0, critical: 'crit' };
  const success = roll <= target;
  if (success) {
    const diff = target - roll;
    return { success: true, dos: 1 + Math.floor(diff / 10), dof: 0, critical: null };
  }
  const diff = roll - target;
  return { success: false, dos: 0, dof: 1 + Math.floor(diff / 10), critical: null };
}

// ── Difficulty ladder ────────────────────────────────────────────────────────
// Six named tiers. Ordinary is the default (table-top tests are "Ordinary"
// unless the situation says otherwise), matching the printed Deathwatch table:
//   Easy +30 / Ordinary +10 / Challenging 0 / Difficult -10 / Hard -20 /
//   Very Hard -30.
const DIFFICULTY = [
  { key: 'easy',        label: 'Easy',        mod: 30 },
  { key: 'ordinary',    label: 'Ordinary',    mod: 10, default: true },
  { key: 'challenging', label: 'Challenging', mod: 0 },
  { key: 'difficult',   label: 'Difficult',   mod: -10 },
  { key: 'hard',        label: 'Hard',        mod: -20 },
  { key: 'veryhard',    label: 'Very Hard',   mod: -30 },
];
// Legacy keys from the pre-A2 four-tier ladder, mapped to the nearest tier so
// any value stored before the change still resolves to a sane modifier.
const DIFFICULTY_LEGACY = { normal: 'challenging', deadly: 'veryhard' };

function difficultyMod(key) {
  const k = DIFFICULTY_LEGACY[key] || key;
  const row = DIFFICULTY.find((d) => d.key === k);
  return row ? row.mod : 0;
}

function defaultDifficulty() {
  const d = DIFFICULTY.find((x) => x.default);
  return d ? d.key : 'challenging';
}

// ── Target clamping ──────────────────────────────────────────────────────────
// A target is always an integer in 0..100 (the percentile scale).
function clampTarget(value) {
  return Math.max(0, Math.min(100, Number(value) || 0));
}

// ── Identity ─────────────────────────────────────────────────────────────────
function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── Fate points ──────────────────────────────────────────────────────────────
// Apply a spent fate point to a recorded test result (one with target /
// attackRoll / success / dos / dof). Returns a new result object; the caller
// is responsible for decrementing/persisting the fate point itself.
//   'reroll' - re-roll the d100 against the same target
//   'plus10' - add +10 to the target and re-evaluate the same roll
//   'dos'    - add one degree of success (turning a failure into a pass)
function applyFateToTest(prev, action) {
  const out = { ...prev, fate: [...(prev.fate || [])] };
  if (action === 'reroll') {
    out.attackRoll = d100();
    const dg = degrees(out.target, out.attackRoll);
    out.success = dg.success; out.dos = dg.dos; out.dof = dg.dof; out.critical = dg.critical;
    out.fate.push('reroll');
  } else if (action === 'plus10') {
    out.target = Math.min(100, (Number(out.target) || 0) + 10);
    const dg = degrees(out.target, out.attackRoll);
    out.success = dg.success; out.dos = dg.dos; out.dof = dg.dof; out.critical = dg.critical;
    out.fate.push('+10');
  } else if (action === 'dos') {
    if (out.success) { out.dos = (Number(out.dos) || 0) + 1; }
    else { out.success = true; out.dos = 1; out.dof = 0; }
    out.fate.push('+DoS');
  }
  return out;
}

// ── Dice expression parsing (damage rolls) ───────────────────────────────────
// Parses "NdF(+N)" expressions, e.g. "2d10+5", "1d10", "1d10-2". Terms are the
// "NdF" parts; `flat` is the running numeric modifier. An empty/blank spec is
// treated as a single d10.
function parseDice(spec) {
  const s = String(spec || '').replace(/\s+/g, '').toLowerCase();
  const parts = s.split('+');
  let flat = 0;
  let terms = [];
  for (const p of parts) {
    if (!p) continue;
    const m = p.match(/^(\d+)d(\d+)$/);
    if (m) {
      const c = parseInt(m[1], 10);
      const f = parseInt(m[2], 10);
      if (!Number.isFinite(c) || !Number.isFinite(f) || c < 1 || f < 2) throw new Error('Invalid dice bounds');
      terms.push({ count: c, faces: f });
    } else {
      const n = Number(p);
      if (!Number.isNaN(n)) flat += n;
      else throw new Error(`Invalid dice term: ${p}`);
    }
  }
  if (terms.length === 0) terms = [{ count: 1, faces: 10 }];
  return { terms, flat };
}

function rollDie(faces) {
  return Math.floor(Math.random() * faces) + 1;
}

// Roll the parsed terms. opts.tearing doubles up (reroll and keep the higher);
// opts.proven applies "Proven" (a d10 cannot come up below `proven`).
function rollDice(terms, opts) {
  const tearing = !!(opts && opts.tearing);
  const proven = Math.max(0, opts && typeof opts.proven === 'number' ? opts.proven : 0);
  const out = [];
  for (const t of terms) {
    for (let i = 0; i < t.count; i++) {
      let r = rollDie(t.faces);
      if (tearing) { const alt = rollDie(t.faces); r = Math.max(r, alt); }
      if (proven > 0 && t.faces === 10) r = Math.max(r, proven);
      out.push(r);
    }
  }
  return { rolls: out, total: out.reduce((a, b) => a + b, 0) };
}

// ── Combat math ──────────────────────────────────────────────────────────────
// Hits a weapon mode inflicts for a given number of Degrees of Success, capped
// by Rate of Fire. single = 1; semi = 1 + 1 per 2 DoS; full = 1 + 1 per DoS.
function hitsFromDoS(mode, dos, rof) {
  const r = rof && rof > 0 ? rof : 1;
  if (mode === 'single') return Math.min(1, r);
  if (mode === 'semi') return Math.max(1, Math.min(1 + Math.floor(dos / 2), r));
  return Math.max(1, Math.min(1 + dos, r));
}

// Hit location from the d100 (reversed-digits) table.
function hitLocationFromRoll(roll) {
  const rev = Number(String(roll).padStart(2, '0').split('').reverse().join(''));
  if (rev >= 1 && rev <= 10) return 'Head';
  if (rev <= 20) return 'Right Arm';
  if (rev <= 30) return 'Left Arm';
  if (rev <= 70) return 'Body';
  if (rev <= 85) return 'Right Leg';
  return 'Left Leg';
}

// Damage after Toughness Bonus and Armour AP are subtracted (floored at 0).
function mitigateDamage(dmg, tb, armour) {
  return Math.max(0, dmg - tb - armour);
}

// ── Tiny CSV parser (used by the bestiary/weapon importers) ─────────────────
function simpleCSVParse(txt) {
  const lines = String(txt ?? '').split(/\r?\n|\r/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const headers = lines[0].split(',').map((h) => h.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(',');
    const obj = {};
    for (let j = 0; j < headers.length; j++) obj[headers[j]] = (cells[j] || '').trim();
    rows.push(obj);
  }
  return rows;
}

export { DIFFICULTY, applyFateToTest, clampTarget, d100, defaultDifficulty, degrees, difficultyMod, hitLocationFromRoll, hitsFromDoS, mitigateDamage, parseDice, rollDice, rollDie, simpleCSVParse, uid };
